import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import styles from "../css/Table.module.css";
import { useNavigate } from "react-router-dom";

export default function TablePendientes() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [openFilters, setOpenFilters] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState([]);
  const [selectedDepto, setSelectedDepto] = useState([]);
  const [selectedMuni, setSelectedMuni] = useState([]);

  const [uniqueUsuarios, setUniqueUsuarios] = useState([]);
  const [uniqueDepartamentos, setUniqueDepartamentos] = useState([]);
  const [uniqueMunicipios, setUniqueMunicipios] = useState([]);
  
  const [isActivePeriod, setIsActivePeriod] = useState(true);
  const [periodMessage, setPeriodMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingPeriod, setCheckingPeriod] = useState(true);

  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const columns = [
    { field: "nombre_completo", headerName: "Registrado por", flex: 0.25 },
    { field: "departamento", headerName: "Departamento", flex: 0.2 },
    { field: "municipio", headerName: "Municipio", flex: 0.2 },
    { field: "fecha_registro", headerName: "Fecha de registro", flex: 0.2 },
    {
      field: "acciones",
      headerName: "Acción",
      flex: 0.1,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => navigate(`/detalle/${params.row.id}`)}
        >
          Ver detalle
        </Button>
      ),
    },
  ];

  const checkActivePeriod = async () => {
    try {
      const formId = 'form2';
      
      if (user) {
        const userParam = encodeURIComponent(JSON.stringify(user));
        const res = await fetch(`/check-period/${formId}?user=${userParam}`);
        const data = await res.json();
        
        setIsActivePeriod(data.isActive);
        setPeriodMessage(data.message);
        setIsAdmin(data.isAdmin || false);
      } else {
        const res = await fetch(`/check-period/${formId}`);
        const data = await res.json();
        
        setIsActivePeriod(data.isActive);
        setPeriodMessage(data.message);
        setIsAdmin(false);
      }
    } catch (err) {
      console.error("Error al verificar periodo:", err);
      setIsActivePeriod(false);
      setPeriodMessage("Error al verificar periodo activo");
      setIsAdmin(false);
    } finally {
      setCheckingPeriod(false);
    }
  };

  useEffect(() => {
    checkActivePeriod();
  }, []);

  useEffect(() => {
    if (isActivePeriod || isAdmin) {
      fetch(`/preguntas/resumen?user=${user.username}`)
        .then((res) => res.json())
        .then((data) => {
          const mapped = data.map((item) => ({
            id: item.preguntaId,
            nombre_completo: item.nombre_completo || "-",
            departamento: item.departamento || "-",
            municipio: item.municipio || "-",
            fecha_registro: item.fecha_registro
              ? new Date(item.fecha_registro).toLocaleDateString("es-ES")
              : "-",
          }));
          setRows(mapped);
          setFilteredRows(mapped);

          setUniqueUsuarios([...new Set(mapped.map((r) => r.nombre_completo))]);
          setUniqueDepartamentos([...new Set(mapped.map((r) => r.departamento))]);
          setUniqueMunicipios([...new Set(mapped.map((r) => r.municipio))]);
        })
        .catch((err) => console.error("Error al obtener datos:", err));
    }
  }, [isActivePeriod, isAdmin]);

  useEffect(() => {
    let filtered = rows;

    if (selectedUsuario.length > 0)
      filtered = filtered.filter((r) => selectedUsuario.includes(r.nombre_completo));

    if (selectedDepto.length > 0)
      filtered = filtered.filter((r) => selectedDepto.includes(r.departamento));

    if (selectedMuni.length > 0)
      filtered = filtered.filter((r) => selectedMuni.includes(r.municipio));

    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter((r) =>
        Object.values(r).some((val) => String(val).toLowerCase().includes(lower))
      );
    }

    setFilteredRows(filtered);
  }, [rows, selectedUsuario, selectedDepto, selectedMuni, searchText]);

  const handleClearFilters = () => {
    setSelectedUsuario([]);
    setSelectedDepto([]);
    setSelectedMuni([]);
    setFilteredRows(rows);
  };

  if (checkingPeriod) {
    return (
      <Paper elevation={6} className={styles.paper}>
        <Typography variant="h5" gutterBottom>
          Respuestas Pendientes
        </Typography>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1">Verificando periodo activo...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!isActivePeriod && !isAdmin) {
    return (
      <Paper elevation={6} className={styles.paper}>
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Período Inactivo
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {periodMessage || "Esta tabla solo está disponible los primeros 6 días de cada mes."}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Por favor, regrese durante el periodo activo para verificar las respuestas.
            </Typography>
          </CardContent>
        </Card>
      </Paper>
    );
  }

  return (
    <Paper elevation={6} className={styles.paper}>
      <Typography variant="h5" gutterBottom>
        Respuestas Pendientes
        {isAdmin && " (Modo Administrador)"}
      </Typography>

      <Alert 
        severity={isAdmin ? "info" : (isActivePeriod ? "success" : "warning")} 
        sx={{ mb: 3 }}
      >
        {isAdmin ? "Modo administrador - Sin restricciones de tiempo" : periodMessage}
      </Alert>

      <Box className={styles.toolbar}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setOpenFilters(true)}
          className={styles.buttonFilter}
        >
          Filtros
        </Button>

        <TextField
          size="small"
          placeholder="Buscar..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "#3949ab", mr: 1 }} />,
          }}
          className={styles.searchField}
        />
      </Box>

      <Box className={styles.dataGridContainer}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[5, 10, 20]}
          disableRowSelectionOnClick
          getRowHeight={() => 50}
          disableColumnMenu
          disableColumnSorting
        />
      </Box>

      <Drawer anchor="right" open={openFilters} onClose={() => setOpenFilters(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <h3>Filtros</h3>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Usuario</InputLabel>
            <Select
              multiple
              value={selectedUsuario}
              onChange={(e) => setSelectedUsuario(e.target.value)}
              input={<OutlinedInput label="Usuario" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {uniqueUsuarios.map((u, i) => (
                <MenuItem key={i} value={u}>
                  <Checkbox checked={selectedUsuario.indexOf(u) > -1} />
                  {u}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Departamento</InputLabel>
            <Select
              multiple
              value={selectedDepto}
              onChange={(e) => setSelectedDepto(e.target.value)}
              input={<OutlinedInput label="Departamento" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {uniqueDepartamentos.map((d, i) => (
                <MenuItem key={i} value={d}>
                  <Checkbox checked={selectedDepto.indexOf(d) > -1} />
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Municipio</InputLabel>
            <Select
              multiple
              value={selectedMuni}
              onChange={(e) => setSelectedMuni(e.target.value)}
              input={<OutlinedInput label="Municipio" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {uniqueMunicipios.map((m, i) => (
                <MenuItem key={i} value={m}>
                  <Checkbox checked={selectedMuni.indexOf(m) > -1} />
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" color="secondary" fullWidth onClick={handleClearFilters}>
            Limpiar filtros
          </Button>
        </Box>
      </Drawer>
    </Paper>
  );
}